import { PerspectiveCamera, WebGLRenderer } from "three";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { SSRPass } from "three/examples/jsm/postprocessing/SSRPass.js";
import { customElement } from "lit/decorators.js";
import { SugarElement } from "../configurator/SugarCanvas";
import { Engine, Entity } from "../engine/Engine";
import { logger } from "../util/Logger";

/**
 * Ekran uzayı çoklu sekme (`bouncing`) ile yaklaşık SSGI.
 * `GTAOPostProcessElement` ile aynı anda kullanma — ikisi de composer sırasına müdahale eder.
 */
class SSGIPostProcessEntity extends Entity {
  private _ssgiPass?: SSRPass;
  private _savedRenderPass?: RenderPass;

  private syncSsrSize(): void {
    const eng = this.engine;
    if (!eng?.renderer.instance || !this._ssgiPass) return;
    const gl = eng.renderer.instance;
    if (!(gl instanceof WebGLRenderer)) return;
    const pr = Math.max(1, gl.getPixelRatio());
    const w = Math.max(1, Math.floor(eng.width * pr));
    const h = Math.max(1, Math.floor(eng.height * pr));
    this._ssgiPass.setSize(w, h);
  }

  private syncSsrFromCamera(): void {
    const eng = this.engine;
    if (!eng?.activeScene || !eng.activeCamera || !this._ssgiPass) return;
    const cam = eng.activeCamera.instance;
    this._ssgiPass.scene = eng.activeScene;
    this._ssgiPass.camera = cam;
    if (cam instanceof PerspectiveCamera) {
      this._ssgiPass.ssrMaterial.uniforms["cameraNear"].value = cam.near;
      this._ssgiPass.ssrMaterial.uniforms["cameraFar"].value = cam.far;
    }
    this._ssgiPass.ssrMaterial.uniforms["cameraProjectionMatrix"].value.copy(
      cam.projectionMatrix,
    );
    this._ssgiPass.ssrMaterial.uniforms["cameraInverseProjectionMatrix"].value.copy(
      cam.projectionMatrixInverse,
    );
  }

  private _updateCameraBound = () => {
    this.syncSsrFromCamera();
  };

  private _engineResizeBound = () => {
    this.syncSsrSize();
  };

  private _interactionStartHandler = () => {
    if (this._ssgiPass) {
      this._ssgiPass.enabled = false;
      this.engine?.dispatchCustomEvent("request-update");
    }
  };

  private _interactionEndHandler = () => {
    if (this._ssgiPass) {
      this._ssgiPass.enabled = true;
      this.engine?.dispatchCustomEvent("request-update");
    }
  };

  private addSSGIPass(): void {
    const eng = this.engine;
    if (!eng?.activeScene || !eng.activeCamera) return;
    if (!(eng.renderer.instance instanceof WebGLRenderer)) return;

    const gl = eng.renderer.instance;
    const pr = Math.max(1, gl.getPixelRatio());
    const w = Math.max(1, Math.floor(eng.width * pr));
    const h = Math.max(1, Math.floor(eng.height * pr));

    this._savedRenderPass = undefined;
    this._ssgiPass = new SSRPass({
      renderer: gl,
      scene: eng.activeScene,
      camera: eng.activeCamera.instance,
      width: w,
      height: h,
      selects: null,
      groundReflector: null,
      isBouncing: true,
      
    });
    this._ssgiPass.opacity = 1.25;

    eng.mutatePassComposer((composer) => {
      const rp = composer.passes.find((p) => p instanceof RenderPass) as
        | RenderPass
        | undefined;
      if (!rp) {
        logger.warn(
          "[SSGIPostProcess] RenderPass yok; composer henüz kurulmamış olabilir.",
        );
        return;
      }
      composer.removePass(rp);
      this._savedRenderPass = rp;
      composer.insertPass(this._ssgiPass!, 0);
    });

    if (!this._savedRenderPass) {
      this._ssgiPass.dispose();
      this._ssgiPass = undefined;
      return;
    }

    eng.addEventListener("update-camera", this._updateCameraBound);
    eng.addEventListener("engine-resize", this._engineResizeBound);
    /*eng.addEventListener("interaction-start", this._interactionStartHandler);
    eng.addEventListener("interaction-end", this._interactionEndHandler);*/
  }

  onAdd(engine: Engine): void {
    this.addSSGIPass();
  }

  onRemove(engine: Engine): void {
    if (this._ssgiPass) {
      engine.mutatePassComposerIfExists((composer) => {
        composer.removePass(this._ssgiPass!);
        if (
          this._savedRenderPass &&
          !composer.passes.includes(this._savedRenderPass)
        ) {
          composer.insertPass(this._savedRenderPass, 0);
        }
      });
      this._ssgiPass.dispose();
      this._ssgiPass = undefined;
    }
    this._savedRenderPass = undefined;
    engine.removeEventListener("update-camera", this._updateCameraBound);
    engine.removeEventListener("engine-resize", this._engineResizeBound);
    engine.removeEventListener("interaction-start", this._interactionStartHandler);
    engine.removeEventListener("interaction-end", this._interactionEndHandler);
  }
}

@customElement("ssgi-postprocess")
export class SSGIPostProcessElement extends SugarElement {
  createEntity(): Entity | undefined {
    return new SSGIPostProcessEntity();
  }
}
